import * as React from "react";
// Utilities
import { format } from "@fluentui/utilities";
import { getTimeZoneDisplayLabel } from "@ms/uno-utilities/lib/local/TimeUtilities";
// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Action Creators
import { ActionCreator, useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
// Controls
import { PlanCalendarNudge } from "@ms/planner-presentational-controls/lib/local/planCalendarNudge/PlanCalendarNudge";
// Hooks
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { useCachedIntlOptions } from "@ms/planner-generic-controls/lib/local/hooks/useCachedTimezone";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
// Models
import { Calendar, DefaultPlanCalendarTimeZone } from "@ms/planner-models/lib/local/client/planner/calendar/Calendar";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { getPlanPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
const ComponentName = "PlanCalendarNudge";
/**
 * Container that shows a Basic Plan calendar settings nudge when the plan still uses default calendar settings.
 * Note: The parent component is expected to gate rendering behind the EnableEvoCalendar flight.
 */ export const PlanCalendarNudgeContainer = ({ planId, onOpenPlanSettings })=>{
    const { traceLogger } = useLoggers();
    const [modernGroupsStore, planStore, appContextStore, permissionsStore, rosterStore, userStore, teamsChannelStore, driveItemStore, todoStore, nudgeStore, dependencyStore, taskStore] = useStores([
        Store.ModernGroups,
        Store.Plan,
        Store.AppContext,
        Store.Permissions,
        Store.Roster,
        Store.User,
        Store.TeamsChannel,
        Store.DriveItem,
        Store.Todo,
        Store.PlanCalendarNudge,
        Store.Dependency,
        Store.Tasks
    ]);
    const [nudgeActionCreator, planActionCreator] = useActionCreators([
        ActionCreator.PlanCalendarNudge,
        ActionCreator.Plan
    ]);
    const { timezone: suggestedTimeZone } = useCachedIntlOptions();
    const permissionUtilityStores = {
        permissionsStore,
        modernGroupsStore,
        rosterStore,
        userStore,
        teamsChannelStore,
        appContextStore,
        planStore,
        driveItemStore,
        todoStore,
        dependencyStore,
        taskStore
    };
    const currentPlanResult = planStore.getPlan(planId);
    const currentPlan = currentPlanResult.data;
    if (!currentPlan || currentPlanResult.status.state !== StoreEntityState.Present) {
        return null;
    }
    // We don't edit Calendar for ToDo plans
    if (currentPlan.getPlanType() === PlanType.ToDo) {
        return null;
    }
    const planPermissions = getPlanPermissionsFromStores(currentPlan, permissionUtilityStores, traceLogger);
    if (!planPermissions.canUpdateCalendar) {
        return null;
    }
    // As per design, we will treat Plan Calendar TimeZone with default as needing to update (i.e., real user should have other values)
    const hasDefaultCalendarSettings = currentPlan.calendar == null || currentPlan.calendar.timeZone === DefaultPlanCalendarTimeZone;
    if (!hasDefaultCalendarSettings || nudgeStore.getIsDismissed(planId)) {
        return null;
    }
    const suggestedTimeZoneLabel = getTimeZoneDisplayLabel(suggestedTimeZone, new Date());
    return /*#__PURE__*/ React.createElement(PlanCalendarNudge, {
        message: format(Strings.PlanCalendarNudge_Message, suggestedTimeZoneLabel),
        updateButtonText: Strings.PlanCalendarNudge_UpdateButton,
        openSettingsButtonText: Strings.PlanCalendarNudge_OpenSettingsButton,
        dismissButtonAriaLabel: Strings.PlanCalendarNudge_DismissAriaLabel,
        onShown: ()=>traceLogger.logTrace(0x1d8106db /* tag_36q11 */ , TraceLevel.Info, `${ComponentName}: nudge shown [PlanId=${planId}]`),
        onUpdate: ()=>{
            traceLogger.logTrace(0x1d80a5a3 /* tag_36kw9 */ , TraceLevel.Info, `${ComponentName}: update clicked [PlanId=${planId}]`);
            const updatedCalendar = (currentPlan.calendar ?? new Calendar()).setProperty("timeZone", suggestedTimeZone);
            const updatedPlan = currentPlan.setProperty("calendar", updatedCalendar);
            planActionCreator.updatePlan(currentPlan, updatedPlan, true);
        },
        onOpenPlanSettings: (elementToFocusOnDismiss)=>{
            traceLogger.logTrace(0x1d8106da /* tag_36q10 */ , TraceLevel.Info, `${ComponentName}: plan settings clicked [PlanId=${planId}]`);
            onOpenPlanSettings(elementToFocusOnDismiss);
        },
        onDismiss: ()=>{
            traceLogger.logTrace(0x1d8106d9 /* tag_36q1z */ , TraceLevel.Info, `${ComponentName}: dismissed [PlanId=${planId}]`);
            void nudgeActionCreator.dismissPlanCalendarNudge(planId);
        }
    });
};
